/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo } from "react";
import { useUrlSearch } from "@/shared/hooks/useUrlSearch";
import { useLinksAggregationQuery, useLinksQuery } from "../api/queries";
import { linksAggregationDefault, searchValues } from "../lib/options";
import type {
  LinksSearchValues,
  PaginationState,
  UseLinksPageReturn,
} from "../types";

export const useLinksPage = (): UseLinksPageReturn => {
  const {
    appliedValues,
    draftValues,
    updateDraft,
    applySearch,
    resetSearch,
    page,
    size,
    setPage,
    setSize,
    showFilters,
    setShowFilters,
  } = useUrlSearch<LinksSearchValues>({
    defaultValues: searchValues as LinksSearchValues,
  });

  const queryParams = useMemo(
    () => ({ page, size, search: appliedValues }),
    [page, size, appliedValues],
  );

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useLinksQuery(queryParams);

  // Keyed by the applied search — refetches itself when the search is applied.
  const { data: aggregation } = useLinksAggregationQuery(appliedValues);

  const data = queryData?.data || [];
  const totalItems = queryData?.totalItems || 0;

  const updatePg = useCallback(
    (newPg: Partial<PaginationState>) => {
      if (newPg.page !== undefined) setPage(newPg.page);
      if (newPg.size !== undefined) setSize(newPg.size);
    },
    [setPage, setSize],
  );

  const updateSearch = useCallback(
    (updates: { show?: boolean; values?: Partial<LinksSearchValues> }) => {
      if (updates.show !== undefined) setShowFilters(updates.show);
      if (updates.values) {
        Object.entries(updates.values).forEach(([key, value]) => {
          updateDraft(key as keyof LinksSearchValues, value as any);
        });
      }
    },
    [setShowFilters, updateDraft],
  );

  // Compat shim: existing list components (useToggle / useDelete / multi
  // actions) expect a `setData` setter. The list is backed by React Query, so
  // any mutation just triggers a refetch instead of mutating local state.
  const setData = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    data,
    isLoading,
    error: error as Error | null,
    totalItems,
    linksAggregation: aggregation ?? linksAggregationDefault,
    search: { show: showFilters, values: draftValues },
    updateSearch,
    handleSearchSubmit: applySearch,
    handleSearchReset: resetSearch,
    pg: { page, size, rows: totalItems },
    updatePg,
    refetch,
    setData: setData as any,
  };
};
